import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import type { LibraryMatchCandidate } from "./MatchPanel";

/**
 * Inline scrollable list of every distinct library plan for a carrier,
 * ranked by trigram similarity to the parsed plan name. Replaces the
 * old popover-based "Wrong match?" UX with a flat, always-visible
 * panel rendered below the RATES strip (or below the FoundPanel).
 *
 * Stateless w.r.t. plan data — caller owns the persist on pick.
 */

interface Props {
  carrierName: string;
  matchedWbId: string | null;
  listCarrierLibrary: () => Promise<LibraryMatchCandidate[]>;
  onPickCandidate: (cand: LibraryMatchCandidate) => void;
}

export function BrowseAlternatesList({
  carrierName,
  matchedWbId,
  listCarrierLibrary,
  onPickCandidate,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LibraryMatchCandidate[]>([]);

  // Fetch once on mount. The parent passes a fresh arrow function on
  // every render, so depending on `listCarrierLibrary` would cause an
  // infinite re-fetch loop (each fetch setState triggers a re-render
  // which creates a new function reference). Component is short-lived
  // (mounted only while disclosure is open), so on-mount is correct.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCarrierLibrary()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const others = items.filter((c) => c.wbId !== matchedWbId);
  const carrierLabel = carrierName?.trim() || "carrier";
  const current = items.find((c) => c.wbId === matchedWbId);

  return (
    <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
      <div className="border-b border-stone-100 bg-stone-50/60 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-stone-500">
        All {carrierLabel} plans · {loading ? "…" : items.length}
      </div>
      <div className="max-h-[280px] overflow-y-auto">
        {loading && (
          <div className="flex items-center gap-2 px-3 py-6 text-[12px] text-stone-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" />
            Loading library…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-3 py-6 text-[12px] italic text-stone-500">
            No library matches for {carrierLabel}.
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="divide-y divide-stone-100">
            {current && (
              <li
                className="flex cursor-default items-center gap-2 bg-emerald-50/40 px-3 py-2"
                aria-disabled
              >
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-emerald-900">
                    {current.name}
                  </div>
                  <div className="text-[10.5px] text-emerald-700">
                    Current pick
                  </div>
                </div>
              </li>
            )}
            {others.map((c) => (
              <li key={c.wbId}>
                <button
                  type="button"
                  onClick={() => onPickCandidate(c)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-stone-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-medium text-stone-900">
                      {c.name}
                    </div>
                    {c.hint && (
                      <div className="truncate text-[10.5px] text-stone-500">
                        {c.hint}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
